import { Injectable, inject } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { WidgetDataSource } from '../../../../../../../models';
import { Comparison, Widget } from '../../../../../../../providers/bridge-api/dashboard.service';
import {
  MatchmakerHandler,
  MatchmakerHandlerPropertyName,
} from '../../../../../../../providers/semantria-api/spote.service';
import { FieldService } from '../../../../../../../providers/storage-api/field.service';
import { EnableClassTelemetry } from '../../../../../../../services/otel.service';
import { ComparisonHandlerService } from '../../../../../../dashboard/services/comparison-handler-service';
import { RatingScoring } from './chart-property-validator';

export interface MatchmakerHandlerValidationResult {
  handler: MatchmakerHandler;
  disabled: boolean;
  reason?: string;
}

export interface Range<T> {
  min: T;
  max?: T;
}

export type NumericRange = Range<number>;

export interface PointSelectionRule {
  rule:
    | 'SAME_SERIES'
    | 'SAME_GROUP'
    | 'ALL_SAME_SERIES'
    | 'ALL_SAME_GROUP'
    | 'UNIQUE_PER_SERIES'
    | 'MAX_PER_SERIES'
    | 'N/A';
  quantifier?: number;
  scoring?: RatingScoring[];
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class WidgetAiInsightsValidatorService {
  handlers: MatchmakerHandler[] = [];
  processedHandlers: MatchmakerHandlerValidationResult[] = [];

  private comparisonHandlerService = inject(ComparisonHandlerService);

  validateMatchmakerHandlers(
    handlers: MatchmakerHandler[],
    widget: Widget,
    dataSource: WidgetDataSource,
    dataPointCount: (handler: MatchmakerHandler) => number
  ) {
    if (handlers) {
      this.handlers = [...handlers];
    }
    return this.revalidateStoredHandlers(widget, dataSource, dataPointCount);
  }

  revalidateStoredHandlers(
    widget: Widget,
    dataSource: WidgetDataSource,
    dataPointCount: (handler: MatchmakerHandler) => number
  ) {
    this.processedHandlers = this.handlers.map((h) =>
      this.isMatchmakerHandlerApplicable(h, widget, dataSource, dataPointCount)
    );
    return this.processedHandlers;
  }

  protected isMatchmakerHandlerApplicable(
    handler: MatchmakerHandler,
    widget: Widget,
    dataSource: WidgetDataSource,
    dataPointCount: (handler: MatchmakerHandler) => number
  ): MatchmakerHandlerValidationResult {
    let result = this.isComparisonCountMatch(handler, widget);
    if (result.disabled) {
      return result;
    }

    result = this.hasMatchingSections(handler, dataSource);
    if (result.disabled) {
      return result;
    }

    const clickablePoints = this.getClickablePoints(handler);
    if (clickablePoints.min > dataPointCount(handler)) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.CLICKABLE_POINTS', {
          count: clickablePoints.min,
        }),
      };
    }

    return {
      handler,
      disabled: false,
    };
  }

  getHandlerProperty(
    handler: MatchmakerHandler,
    propName: MatchmakerHandlerPropertyName,
    keepCase?
  ) {
    return this.getHandlerPropertyList(handler, propName, keepCase)[0];
  }

  getHandlerPropertyList(
    handler: MatchmakerHandler,
    propName: MatchmakerHandlerPropertyName,
    keepCase?
  ) {
    let props = handler.properties
      .filter((h) => h.property_key === propName)
      .map((h) => h.property_value);
    if (!keepCase) {
      props = props.map((p) => p.toLocaleUpperCase());
    }
    return props;
  }

  protected isComparisonCountMatch(
    handler: MatchmakerHandler,
    widget: Widget
  ): MatchmakerHandlerValidationResult {
    const handlerComparisonCount = this.getHandlerProperty(handler, 'ADDITIONAL_COMPARISONS');

    if (
      !handlerComparisonCount ||
      handlerComparisonCount === 'N/A' ||
      handlerComparisonCount === 'ANY'
    ) {
      return {
        handler,
        disabled: false,
      };
    }

    const widgetComparison = this.comparisonHandlerService.getComparison(widget.payload.comparison);
    // we will allow widgets with no comparison to be applied to a handler having ADDITIONAL_COMPARISONS=1
    const isComparisonCountMatch =
      (widgetComparison?.filters?.length || 1) === (+handlerComparisonCount || 1);

    return {
      handler,
      disabled: !isComparisonCountMatch,
      reason:
        !isComparisonCountMatch &&
        translate('widgets.ai-insights.handler-tooltips.ADDITIONAL_COMPARISONS', {
          count: +handlerComparisonCount,
        }),
    };
  }

  protected hasMatchingSections(
    handler: MatchmakerHandler,
    dataSource: WidgetDataSource
  ): MatchmakerHandlerValidationResult {
    const sectionInfo = this.getHandlerProperty(handler, 'SECTION_DATA', true);

    if (!sectionInfo || sectionInfo.toLocaleUpperCase() === 'ALL') {
      return {
        handler,
        disabled: false,
      };
    }

    if (sectionInfo.toLocaleUpperCase() === 'FALSE') {
      return {
        handler,
        disabled: !!dataSource?.hasSections,
        reason:
          !!dataSource?.hasSections &&
          translate('widgets.ai-insights.handler-tooltips.SECTION_DATA.no-sections'),
      };
    }

    if (!dataSource?.hasSections) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.SECTION_DATA.has-sections'),
      };
    }

    if (sectionInfo.toLocaleUpperCase() === 'TRUE') {
      return {
        handler,
        disabled: false,
      };
    }

    const sectionSeparator = sectionInfo.split(':');
    const fieldNames = dataSource.sectionFieldNames.map((f) => FieldService.getFieldLongName(f));

    if (
      !fieldNames.some(
        (f) => f.localeCompare(sectionSeparator[0], undefined, { sensitivity: 'accent' }) === 0
      )
    ) {
      return {
        handler,
        disabled: true,
        reason: translate(
          'widgets.ai-insights.handler-tooltips.SECTION_DATA.missing-section-separator'
        ),
      };
    }

    return {
      handler,
      disabled: false,
    };
  }

  isDocCardinalityMultiple(handler: MatchmakerHandler) {
    return this.getHandlerProperty(handler, 'DOC_CARDINALTY') === 'MULTIPLE';
  }

  isTimeSeriesRequired(handler: MatchmakerHandler) {
    return this.getHandlerProperty(handler, 'TIME_SERIES_REQUIRED') === 'TRUE';
  }

  isRatingRequired(handler: MatchmakerHandler) {
    return this.getHandlerProperty(handler, 'RATING_REQUIRED') === 'TRUE';
  }

  getClickablePoints(handler: MatchmakerHandler): NumericRange {
    const clickablePoints = this.getHandlerProperty(handler, 'CLICKABLE_POINTS').split(':');
    const min = clickablePoints[0];
    if (clickablePoints.length === 1) {
      return {
        min: +min,
        max: +min,
      };
    }

    return {
      min: +min,
      max: +clickablePoints[1] || Number.MAX_SAFE_INTEGER,
    };
  }

  isApplyComparison(handler: MatchmakerHandler, comparison?: Comparison) {
    if (!comparison || comparison.filters.length <= 1) {
      return false;
    }
    const comparisons = this.getHandlerProperty(handler, 'ADDITIONAL_COMPARISONS');
    return comparisons && comparisons !== 'N/A';
  }

  getPointSelectionRule(handler: MatchmakerHandler): PointSelectionRule {
    const ratingCalculation = this.getHandlerPropertyList(
      handler,
      'RATING_CALCULATION'
    ) as RatingScoring[];
    const cpRules = this.getHandlerProperty(handler, 'CLICKABLE_POINTS_RULES');

    const ruleParts = cpRules?.split(':') || ['N/A'];
    const rule: PointSelectionRule = {
      rule: ruleParts[0] as PointSelectionRule['rule'],
      quantifier: 1,
      scoring: ratingCalculation,
    };

    if (ruleParts[1]) {
      rule.quantifier = +ruleParts[1];
    }

    return rule;
  }
}
